import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  AlertTitle,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Editor } from '@tinymce/tinymce-react';
import { fetchData } from '../../helpers/apiUtils';
import { ParentCampaignOption } from '../../helpers/webinarMailingData';
import moment from 'moment';
import { useUrlValidation } from '../../hooks/useUrlValidation';
import { validatePlaceholders, FOLLOWUP_PLACEHOLDERS } from '../../helpers/placeholderValidation';
import SpamScorePanel, { CombinedResult as SpamScoreResult } from '../../components/SpamScorePanel';
import { recordDecision } from '../../helpers/spamWords/spamScoreApi';

/**
 * Follow-up Editor V2
 * - Unified campaign with RNA + ANO segments
 * - FT/Iterable only (no multi-client)
 * - Each segment has own landing URL, delay, and emails
 */

interface FollowupEditorProps {
  campaignId: number | null;
  onSaved: () => void;
  onCancel: () => void;
}

type SegmentType = 'non_attendee' | 'attendee_no_purchase';

interface SegmentData {
  id: number;
  segmentType: SegmentType;
  landingPageUrl: string;
  delayDays: number;
  delayTime: string;
  enabled: boolean;
  recipientCount: number;
  status: string;
  emails: EmailData[];
}

interface EmailData {
  id?: number;
  mailingNumber: number;
  subject: string;
  fromName: string;
  previewText: string;
  htmlBody: string;
  delayOffsetDays: number;
}

interface SegmentPreview {
  segmentType: SegmentType;
  registrations: number;
  attendees: number;
  purchasers: number;
  segmentSize: number;
}

const DEFAULT_FROM_NAME = 'Finanztrends';

const createDefaultEmail = (num: number): EmailData => ({
  mailingNumber: num,
  subject: '',
  fromName: DEFAULT_FROM_NAME,
  previewText: '',
  htmlBody: '',
  delayOffsetDays: num - 1,
});

const createDefaultSegment = (type: SegmentType, id: number): SegmentData => ({
  id,
  segmentType: type,
  landingPageUrl: '',
  delayDays: 1,
  // 12:00 seit 07.09.2026 (vorher 10:00): einziger Slot ohne eigenen Wettbewerb —
  // 06:00 SAM-Versand, 09:00 FT-Newsletter. Backend-Gegenstueck:
  // node-app helpers/webinarFollowupRegeln.ts FOLLOWUP_DEFAULT_SENDEZEIT.
  delayTime: '12:00',
  enabled: true,
  recipientCount: 0,
  status: 'draft',
  emails: [createDefaultEmail(1)],
});

const FollowupEditor: React.FC<FollowupEditorProps> = ({ campaignId, onSaved, onCancel }) => {
  // Campaign state
  const [parentCampaignId, setParentCampaignId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<string>('draft');

  // Segments state
  const [rnaSegment, setRnaSegment] = useState<SegmentData>(
    createDefaultSegment('non_attendee', 0),
  );
  const [anoSegment, setAnoSegment] = useState<SegmentData>(
    createDefaultSegment('attendee_no_purchase', 0),
  );
  const [activeSegmentTab, setActiveSegmentTab] = useState<0 | 1>(0); // 0=RNA, 1=ANO
  const [activeEmailTab, setActiveEmailTab] = useState(0);

  // Lookup data
  const [parentCampaigns, setParentCampaigns] = useState<ParentCampaignOption[]>([]);
  const [rnaPreview, setRnaPreview] = useState<SegmentPreview | null>(null);
  const [anoPreview, setAnoPreview] = useState<SegmentPreview | null>(null);

  // URL validation
  const {
    validateUrl,
    getState,
    resetAll: resetUrlValidation,
    hasUnreachableUrls,
    isValidating,
  } = useUrlValidation();
  const landingPageUrlState = getState('landing_page_url');

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Spam-score state (keyed by "segment_label:email_index")
  const [spamResults, setSpamResults] = useState<Record<string, SpamScoreResult>>({});
  const [spamOverrideOpen, setSpamOverrideOpen] = useState(false);
  const [spamOverridePayload, setSpamOverridePayload] = useState<{
    segmentLabel: string;
    emailIndex: number;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const [spamAdvisoryOpen, setSpamAdvisoryOpen] = useState(false);
  const [spamAdvisoryPayload, setSpamAdvisoryPayload] = useState<{
    segmentLabel: string;
    emailIndex: number;
    warningCount: number;
    llmChecked: boolean;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const handleSpamResultChange = useCallback((key: string, result: SpamScoreResult) => {
    setSpamResults((prev) => ({ ...prev, [key]: result }));
  }, []);

  // Import template state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importedContent, setImportedContent] = useState<string | null>(null);
  const [detectedUrls, setDetectedUrls] = useState<string[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewWidth, setPreviewWidth] = useState<'desktop' | 'mobile'>('desktop');

  // Test send state
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  // Cancel campaign state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Reschedule campaign state
  const [rescheduling, setRescheduling] = useState(false);

  // Schedule success state
  const [scheduleSuccess, setScheduleSuccess] = useState(false);

  const editorRefs = useRef<Record<string, any>>({});

  const activeSegment = activeSegmentTab === 0 ? rnaSegment : anoSegment;
  const setActiveSegment = activeSegmentTab === 0 ? setRnaSegment : setAnoSegment;

  // Load data on mount
  useEffect(() => {
    loadParentCampaigns();
    if (campaignId) {
      loadCampaign(campaignId);
    }
  }, [campaignId]);

  // Load segment previews when parent campaign changes
  useEffect(() => {
    if (parentCampaignId) {
      const parent = parentCampaigns.find((c) => c.id === parentCampaignId);
      if (parent?.webinar_id) {
        loadSegmentPreviews(parent.webinar_id);
      }
    }
  }, [parentCampaignId, parentCampaigns]);

  const loadParentCampaigns = async () => {
    try {
      const response = await fetchData<ParentCampaignOption[]>(
        '/api/webinar-followups/parent-campaigns',
      );
      if (response.success && response.data) {
        setParentCampaigns(response.data);
      }
    } catch (err) {
      console.error('Failed to load parent campaigns:', err);
    }
  };

  const loadCampaign = async (id: number) => {
    setLoading(true);
    try {
      const response = await fetchData<{
        campaign: any;
        segments: any[];
        webinar: any;
      }>(`/api/webinar-followups/campaigns/${id}`);

      if (response.success && response.data) {
        const { campaign, segments } = response.data;

        setParentCampaignId(campaign.parentCampaignId);
        setName(campaign.name);
        setStatus(campaign.status);

        // Map segments
        segments.forEach((seg) => {
          const segmentData: SegmentData = {
            id: seg.id,
            segmentType: seg.segmentType,
            landingPageUrl: seg.landingPageUrl || '',
            delayDays: seg.delayDays || 1,
            delayTime: seg.delayTime?.substring(0, 5) || '12:00',
            enabled: seg.enabled ?? true,
            recipientCount: seg.recipientCount || 0,
            status: seg.status || 'draft',
            emails: seg.emails?.map((e: any) => ({
              id: e.id,
              mailingNumber: e.mailingNumber,
              subject: e.subject || '',
              fromName: e.fromName || DEFAULT_FROM_NAME,
              previewText: e.previewText || '',
              htmlBody: e.htmlBody || '',
              delayOffsetDays: e.delayOffsetDays || 0,
            })) || [createDefaultEmail(1)],
          };

          if (seg.segmentType === 'non_attendee') {
            setRnaSegment(segmentData);
          } else {
            setAnoSegment(segmentData);
          }
        });
      }
    } catch (err) {
      setError('Fehler beim Laden der Kampagne');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSegmentPreviews = async (webinarId: string) => {
    try {
      const [rnaRes, anoRes] = await Promise.all([
        fetchData<{ data: SegmentPreview }>(
          `/api/webinar-followups/segment-preview?webinar_id=${webinarId}&segment_type=non_attendee`,
        ),
        fetchData<{ data: SegmentPreview }>(
          `/api/webinar-followups/segment-preview?webinar_id=${webinarId}&segment_type=attendee_no_purchase`,
        ),
      ]);

      if (rnaRes.success && rnaRes.data) {
        setRnaPreview({
          segmentType: 'non_attendee',
          registrations: (rnaRes.data as any).registrations,
          attendees: (rnaRes.data as any).attendees,
          purchasers: (rnaRes.data as any).purchasers,
          segmentSize: (rnaRes.data as any).total_emails || (rnaRes.data as any).segment_size,
        });
      }

      if (anoRes.success && anoRes.data) {
        setAnoPreview({
          segmentType: 'attendee_no_purchase',
          registrations: (anoRes.data as any).registrations,
          attendees: (anoRes.data as any).attendees,
          purchasers: (anoRes.data as any).purchasers,
          segmentSize: (anoRes.data as any).total_emails || (anoRes.data as any).segment_size,
        });
      }
    } catch (err) {
      console.error('Failed to load segment previews:', err);
    }
  };

  // Refresh the webinar order mapping cache and reload previews
  const refreshOrderMappingCache = async () => {
    if (!selectedParent) return;

    setRefreshingCache(true);
    try {
      // Trigger cache update
      const res = await fetchData<{ success: boolean }>(
        '/api/webinar-followups/refresh-order-mapping',
      );
      if (res.success) {
        // Reload segment previews with fresh data
        await loadSegmentPreviews(selectedParent.webinar_id);
      }
    } catch (err) {
      console.error('Failed to refresh order mapping cache:', err);
    } finally {
      setRefreshingCache(false);
    }
  };

  // Get editor content synchronously - returns values directly instead of relying on async state updates
  const getEditorContent = (): {
    rnaEmails: EmailData[];
    anoEmails: EmailData[];
  } => {
    const prefix = activeSegmentTab === 0 ? 'rna' : 'ano';

    // Build RNA emails - use current editor content if RNA tab is active, otherwise use state
    const rnaEmails = rnaSegment.emails.map((email, index) => {
      const key = `rna_${index}`;
      if (prefix === 'rna' && editorRefs.current[key]) {
        return { ...email, htmlBody: editorRefs.current[key].getContent() };
      }
      return email; // Keep existing state for unmounted editors
    });

    // Build ANO emails - use current editor content if ANO tab is active, otherwise use state
    const anoEmails = anoSegment.emails.map((email, index) => {
      const key = `ano_${index}`;
      if (prefix === 'ano' && editorRefs.current[key]) {
        return { ...email, htmlBody: editorRefs.current[key].getContent() };
      }
      return email;
    });

    return { rnaEmails, anoEmails };
  };

  // Sync only the current segment's editors (for tab switch - other tab's editors are unmounted)
  const syncCurrentSegmentEditors = () => {
    const prefix = activeSegmentTab === 0 ? 'rna' : 'ano';
    const setter = activeSegmentTab === 0 ? setRnaSegment : setAnoSegment;
    const currentSegment = activeSegmentTab === 0 ? rnaSegment : anoSegment;

    const updatedEmails = [...currentSegment.emails];
    updatedEmails.forEach((_, index) => {
      const key = `${prefix}_${index}`;
      if (editorRefs.current[key]) {
        updatedEmails[index].htmlBody = editorRefs.current[key].getContent();
      }
    });
    setter((prev) => ({ ...prev, emails: updatedEmails }));
  };

  const handleSegmentTabChange = (_: React.SyntheticEvent, newTab: 0 | 1) => {
    // Sync current segment's editor content BEFORE switching (other tab's editors are unmounted)
    syncCurrentSegmentEditors();
    setActiveSegmentTab(newTab);
    setActiveEmailTab(0);
  };

  const handleSave = async (skipNavigation = false): Promise<boolean> => {
    if (!parentCampaignId || !name) {
      setError('Bitte alle Pflichtfelder ausfüllen');
      return false;
    }

    // Spam-Score hard-block check across both segments (only enabled with recipients)
    const spamChecks: { label: string; index: number; key: string }[] = [];
    if (rnaSegment.enabled && rnaPreview && rnaPreview.segmentSize > 0) {
      rnaSegment.emails.forEach((_, idx) =>
        spamChecks.push({ label: 'RNA', index: idx, key: `rna:${idx}` }),
      );
    }
    if (anoSegment.enabled && anoPreview && anoPreview.segmentSize > 0) {
      anoSegment.emails.forEach((_, idx) =>
        spamChecks.push({ label: 'ANO', index: idx, key: `ano:${idx}` }),
      );
    }
    const overriddenKeys = new Set<string>();
    for (const c of spamChecks) {
      const spam = spamResults[c.key];
      if (spam?.shouldHardBlock) {
        setActiveSegmentTab(c.label === 'RNA' ? 0 : 1);
        setActiveEmailTab(c.index);
        const userConfirmed = await new Promise<boolean>((resolve) => {
          setSpamOverridePayload({
            segmentLabel: c.label,
            emailIndex: c.index,
            resolve,
          });
          setSpamOverrideOpen(true);
        });
        if (!userConfirmed) {
          setError(
            `${c.label} E-Mail ${c.index + 1}: Content-Quality-Check nicht bestanden (${spam.layer1.redCount} Red-Flags) — bitte Begriffe ersetzen.`,
          );
          return false;
        }
        overriddenKeys.add(c.key);
      }
    }

    // Spam-Score advisory (non-blocking): warn if L1 warnings present and
    // either no LLM check run, stale check, or LLM score < 70.
    for (const c of spamChecks) {
      const spam = spamResults[c.key];
      if (!spam || spam.shouldHardBlock) continue;
      const warningCount = spam.layer1.flaggedPhrases.length + spam.layer1.patternHits.length;
      if (warningCount === 0) continue;
      const llmChecked = spam.layer2Score != null && !spam.contentHashStale;
      if (llmChecked && spam.layer2Score! >= 70) continue;
      setActiveSegmentTab(c.label === 'RNA' ? 0 : 1);
      setActiveEmailTab(c.index);
      const userProceed = await new Promise<boolean>((resolve) => {
        setSpamAdvisoryPayload({
          segmentLabel: c.label,
          emailIndex: c.index,
          warningCount,
          llmChecked,
          resolve,
        });
        setSpamAdvisoryOpen(true);
      });
      if (!userProceed) return false;
    }

    // Get editor content synchronously BEFORE any async operations
    const { rnaEmails, anoEmails } = getEditorContent();
    setSaving(true);
    setError(null);

    try {
      let savedCampaignId = campaignId;
      let rnaSegmentId = rnaSegment.id;
      let anoSegmentId = anoSegment.id;

      if (!savedCampaignId) {
        // Create new campaign (auto-creates both segments)
        const createResponse = await fetchData<{
          campaignId: number;
          segments: { rna: number; ano: number };
        }>('/api/webinar-followups/campaigns', {
          method: 'POST',
          body: JSON.stringify({
            parent_campaign_id: parentCampaignId,
            name,
          }),
        });

        if (!createResponse.success || !createResponse.data?.campaignId) {
          throw new Error(createResponse.error || 'Fehler beim Erstellen');
        }

        savedCampaignId = createResponse.data.campaignId;
        rnaSegmentId = createResponse.data.segments.rna;
        anoSegmentId = createResponse.data.segments.ano;

        // Update local segment IDs (async, for UI)
        setRnaSegment((prev) => ({ ...prev, id: rnaSegmentId }));
        setAnoSegment((prev) => ({ ...prev, id: anoSegmentId }));
      } else {
        // Update campaign name
        const updateResponse = await fetchData(
          `/api/webinar-followups/campaigns/${savedCampaignId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ name }),
          },
        );

        if (!updateResponse.success) {
          throw new Error(updateResponse.error || 'Fehler beim Aktualisieren');
        }
      }

      // Save RNA segment config and emails (using synchronously fetched email content)
      await fetchData(`/api/webinar-followups/segments/${rnaSegmentId}`, {
        method: 'PUT',
        body: JSON.stringify({
          landing_page_url: rnaSegment.landingPageUrl,
          delay_days: rnaSegment.delayDays,
          delay_time: rnaSegment.delayTime + ':00',
          enabled: rnaSegment.enabled,
        }),
      });

      await fetchData(`/api/webinar-followups/segments/${rnaSegmentId}/emails`, {
        method: 'POST',
        body: JSON.stringify({
          emails: rnaEmails.map((e) => ({
            mailing_number: e.mailingNumber,
            subject: e.subject,
            from_name: e.fromName,
            preview_text: e.previewText,
            html_body: e.htmlBody,
            delay_offset_days: e.delayOffsetDays,
          })),
        }),
      });

      // Save ANO segment config and emails (using synchronously fetched email content)
      await fetchData(`/api/webinar-followups/segments/${anoSegmentId}`, {
        method: 'PUT',
        body: JSON.stringify({
          landing_page_url: anoSegment.landingPageUrl,
          delay_days: anoSegment.delayDays,
          delay_time: anoSegment.delayTime + ':00',
          enabled: anoSegment.enabled,
        }),
      });

      await fetchData(`/api/webinar-followups/segments/${anoSegmentId}/emails`, {
        method: 'POST',
        body: JSON.stringify({
          emails: anoEmails.map((e) => ({
            mailing_number: e.mailingNumber,
            subject: e.subject,
            from_name: e.fromName,
            preview_text: e.previewText,
            html_body: e.htmlBody,
            delay_offset_days: e.delayOffsetDays,
          })),
        }),
      });

      // Record user_decision for each email's spam check
      spamChecks.forEach((c) => {
        const spam = spamResults[c.key];
        if (!spam) return;
        const decision = overriddenKeys.has(c.key) ? 'overridden' : 'accepted';
        recordDecision('webinar_followup', c.index + 1, decision, savedCampaignId ?? null).catch(
          (err) => console.warn('recordDecision failed', err),
        );
      });

      if (!skipNavigation) {
        onSaved();
      }
      return true;
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSchedule = async () => {
    setSaving(true);
    setError(null);

    try {
      // First save
      const saveSuccess = await handleSave(true);
      if (!saveSuccess) {
        setSaving(false);
        return;
      }

      // Then schedule
      const response = await fetchData<{
        scheduled: any[];
        skipped: any[];
        awaitingWebinar?: boolean;
        expectedScheduleDate?: string;
        message?: string;
      }>(`/api/webinar-followups/campaigns/${campaignId}/schedule`, {
        method: 'POST',
      });

      if (!response.success) {
        throw new Error(response.error || 'Fehler beim Planen');
      }

      const { scheduled, skipped, awaitingWebinar, message } = response.data!;

      if (awaitingWebinar) {
        // Webinar hasn't happened yet - show info and refresh status
        alert(
          message ||
            'Webinar noch nicht stattgefunden. Mailings werden automatisch nach dem Webinar geplant.',
        );
        setStatus('awaiting_webinar');
        setSaving(false);
      } else if (scheduled.length === 0 && skipped.length > 0) {
        setError(
          `Keine Segmente geplant. Gründe: ${skipped.map((s) => `${s.segmentType}: ${s.reason}`).join(', ')}`,
        );
        setSaving(false);
      } else {
        // Show success message and delay redirect
        setScheduleSuccess(true);
        setTimeout(() => onSaved(), 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Planen');
      setSaving(false);
    }
  };

  const handleCancelCampaign = async () => {
    if (!campaignId) return;

    setCancelling(true);
    setError(null);

    try {
      const response = await fetchData<{
        campaign_id: number;
        campaign_status: string;
        total_cancelled: number;
        total_already_sent: number;
      }>(`/api/webinar-followups/campaigns/${campaignId}/cancel`, {
        method: 'POST',
      });

      if (!response.success) {
        throw new Error(response.error || 'Fehler beim Abbrechen');
      }

      const { total_cancelled, total_already_sent } = response.data!;
      let message = `Kampagne abgebrochen: ${total_cancelled} E-Mail(s) storniert`;
      if (total_already_sent > 0) {
        message += `, ${total_already_sent} bereits gesendet (nicht stornierbar)`;
      }
      alert(message);
      onSaved(); // Return to list
    } catch (err: any) {
      setError(err.message || 'Fehler beim Abbrechen');
    } finally {
      setCancelling(false);
      setShowCancelDialog(false);
    }
  };

  const handleReschedule = async () => {
    if (!campaignId) return;

    setRescheduling(true);
    setError(null);

    try {
      const response = await fetchData<{
        campaign_id: number;
        new_status: string;
        segments_reset: number;
        emails_cleared: number;
      }>(`/api/webinar-followups/campaigns/${campaignId}/reschedule`, {
        method: 'POST',
      });

      if (!response.success) {
        throw new Error(response.error || 'Fehler beim Freigeben');
      }

      // Reload the campaign - it's now in draft status and editable
      await loadCampaign(campaignId);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Freigeben zur Bearbeitung');
    } finally {
      setRescheduling(false);
    }
  };

  const addEmail = () => {
    const segment = activeSegment;
    if (segment.emails.length >= 3) return;

    const newEmail = createDefaultEmail(segment.emails.length + 1);
    setActiveSegment({ ...segment, emails: [...segment.emails, newEmail] });
    setActiveEmailTab(segment.emails.length);
  };

  const removeEmail = (index: number) => {
    const segment = activeSegment;
    if (segment.emails.length <= 1) return;

    const newEmails = segment.emails
      .filter((_, i) => i !== index)
      .map((e, i) => ({ ...e, mailingNumber: i + 1 }));

    setActiveSegment({ ...segment, emails: newEmails });
    setActiveEmailTab(Math.max(0, activeEmailTab - 1));
  };

  const updateEmail = (index: number, field: keyof EmailData, value: any) => {
    const segment = activeSegment;
    const newEmails = [...segment.emails];
    (newEmails[index] as any)[field] = value;
    setActiveSegment({ ...segment, emails: newEmails });
  };

  // ============================================================================
  // IMPORT HANDLERS
  // ============================================================================

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      setError('Bitte wählen Sie eine HTML-Datei aus');
      return;
    }

    setImportLoading(true);
    setError(null);

    try {
      const htmlContent = await file.text();

      const response = await fetchData<{
        extractedContent: string;
        detectedUrls: string[];
      }>('/api/webinar-mailings/import-template', {
        method: 'POST',
        body: JSON.stringify({ htmlContent }),
      });

      if (response.success && response.data) {
        setImportedContent(response.data.extractedContent);
        setDetectedUrls(response.data.detectedUrls);
        setSelectedUrls(response.data.detectedUrls);
      } else {
        setError(response.error || 'Fehler beim Importieren');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Importieren');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmImport = () => {
    if (!importedContent) return;

    let content = importedContent;
    selectedUrls.forEach((url) => {
      const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      content = content.replace(new RegExp(escapedUrl, 'g'), '{FOLLOWUP_LINK}');
    });

    // Update current segment's current email
    const segment = activeSegment;
    const newEmails = [...segment.emails];
    newEmails[activeEmailTab].htmlBody = content;
    setActiveSegment({ ...segment, emails: newEmails });

    // Update TinyMCE editor
    const editorKey = activeSegmentTab === 0 ? `rna_${activeEmailTab}` : `ano_${activeEmailTab}`;
    if (editorRefs.current[editorKey]) {
      editorRefs.current[editorKey].setContent(content, { format: 'raw' });
    }

    setImportModalOpen(false);
    setImportedContent(null);
    setDetectedUrls([]);
    setSelectedUrls([]);
  };

  // ============================================================================
  // PREVIEW HANDLERS
  // ============================================================================

  const handlePreview = async () => {
    if (!activeSegment.id) {
      setError('Segment muss zuerst gespeichert werden');
      return;
    }

    const currentEmail = activeSegment.emails[activeEmailTab];
    if (!currentEmail?.htmlBody) {
      setError('E-Mail hat keinen HTML-Inhalt');
      return;
    }

    setLoadingPreview(true);

    const saveSuccess = await handleSave(true);
    if (!saveSuccess) {
      setLoadingPreview(false);
      return;
    }

    try {
      const parent = parentCampaigns.find((c) => c.id === parentCampaignId);
      const response = await fetchData<{ html_body: string }>(
        `/api/webinar-followups/segments/${activeSegment.id}/preview/${currentEmail.mailingNumber}?webinar_id=${parent?.webinar_id}`,
      );

      if (response.success && response.data) {
        setPreviewHtml(response.data.html_body);
        setShowPreviewDialog(true);
      } else {
        setError(response.error || 'Fehler beim Laden der Vorschau');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Vorschau');
    } finally {
      setLoadingPreview(false);
    }
  };

  // ============================================================================
  // TEST SEND HANDLERS
  // ============================================================================

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setError('Bitte E-Mail-Adresse eingeben');
      return;
    }

    if (!activeSegment.id) {
      setError('Segment muss zuerst gespeichert werden');
      return;
    }

    const currentEmail = activeSegment.emails[activeEmailTab];
    if (!currentEmail?.subject || !currentEmail?.htmlBody) {
      setError('E-Mail muss Betreff und HTML-Inhalt haben');
      return;
    }

    setSendingTest(true);

    const saveSuccess = await handleSave(true);
    if (!saveSuccess) {
      setSendingTest(false);
      return;
    }

    try {
      const parent = parentCampaigns.find((c) => c.id === parentCampaignId);
      const response = await fetchData(
        `/api/webinar-followups/segments/${activeSegment.id}/send-test`,
        {
          method: 'POST',
          body: JSON.stringify({
            mailing_number: currentEmail.mailingNumber,
            test_email: testEmail,
            webinar_id: parent?.webinar_id,
          }),
        },
      );

      if (response.success) {
        setShowTestDialog(false);
        setTestEmail('');
        alert(`Test-E-Mail erfolgreich gesendet an ${testEmail}`);
      } else {
        setError(response.error || 'Fehler beim Senden der Test-E-Mail');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Senden der Test-E-Mail');
    } finally {
      setSendingTest(false);
    }
  };

  const selectedParent = parentCampaigns.find((c) => c.id === parentCampaignId);
  const currentPreview = activeSegmentTab === 0 ? rnaPreview : anoPreview;

  // Validation: Check if all emails in enabled segments have required content
  const MIN_CONTENT_LENGTH = 1000;

  const validateSegmentEmails = (segment: SegmentData, segmentName: string): string[] => {
    const issues: string[] = [];
    segment.emails.forEach((email, idx) => {
      const emailNum = idx + 1;
      if (!email.subject || email.subject.trim().length === 0) {
        issues.push(`${segmentName} E-Mail ${emailNum}: Betreff fehlt`);
      }
      if (!email.htmlBody || email.htmlBody.trim().length === 0) {
        issues.push(`${segmentName} E-Mail ${emailNum}: Inhalt fehlt`);
      } else {
        if (email.htmlBody.length < MIN_CONTENT_LENGTH) {
          issues.push(
            `${segmentName} E-Mail ${emailNum}: Inhalt zu kurz (${email.htmlBody.length}/${MIN_CONTENT_LENGTH} Zeichen)`,
          );
        }
        const placeholderIssues = validatePlaceholders(email.htmlBody, email.subject || '', {
          allowed: FOLLOWUP_PLACEHOLDERS,
          required: 'FOLLOWUP_LINK',
          system: 'followup',
          caseSensitive: false,
        });
        for (const issue of placeholderIssues) {
          issues.push(`${segmentName} E-Mail ${emailNum}: ${issue}`);
        }
      }
    });
    return issues;
  };

  const getValidationIssues = (): string[] => {
    const issues: string[] = [];

    // Check RNA segment if enabled and has recipients
    if (rnaSegment.enabled && rnaPreview && rnaPreview.segmentSize > 0) {
      issues.push(...validateSegmentEmails(rnaSegment, 'RNA'));
    }

    // Check ANO segment if enabled and has recipients
    if (anoSegment.enabled && anoPreview && anoPreview.segmentSize > 0) {
      issues.push(...validateSegmentEmails(anoSegment, 'ANO'));
    }

    return issues;
  };

  const validationIssues = getValidationIssues();
  const hasEnabledSegmentWithRecipients =
    (rnaSegment.enabled && rnaPreview && rnaPreview.segmentSize > 0) ||
    (anoSegment.enabled && anoPreview && anoPreview.segmentSize > 0);
  const canSchedule = hasEnabledSegmentWithRecipients && validationIssues.length === 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" mb={3}>
        {campaignId ? 'Nachfass-Kampagne bearbeiten' : 'Neue Nachfass-Kampagne'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {scheduleSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Kampagne erfolgreich geplant! Weiterleitung...
        </Alert>
      )}

      {status === 'awaiting_webinar' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Wartet auf Webinar:</strong> Das Webinar hat noch nicht stattgefunden. Die
          Mailings werden automatisch um 02:00 Uhr am Tag nach dem Webinar geplant. Sie können die
          Inhalte bis dahin noch bearbeiten.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Parent Campaign */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth disabled={!!campaignId}>
            <InputLabel>Parent-Kampagne (Webinar)</InputLabel>
            <Select
              value={parentCampaignId}
              label="Parent-Kampagne (Webinar)"
              onChange={(e) => setParentCampaignId(e.target.value as number)}
            >
              {parentCampaigns.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} - {c.webinar_title || 'Kein Webinar'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Campaign Name */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Kampagnenname"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={status !== 'draft'}
          />
        </Grid>

        {/* Segment Overview */}
        {selectedParent && (rnaPreview || anoPreview) && (
          <Grid item xs={12}>
            <Alert severity="info" icon={false}>
              <AlertTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <span>Segment-Vorschau ({selectedParent.webinar_title})</span>
                  <Button
                    size="small"
                    startIcon={refreshingCache ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={refreshOrderMappingCache}
                    disabled={refreshingCache}
                    sx={{ ml: 2 }}
                  >
                    {refreshingCache ? 'Aktualisiere...' : 'Käufer aktualisieren'}
                  </Button>
                </Box>
              </AlertTitle>
              <Box display="flex" gap={4}>
                <Box>
                  <Typography variant="body2">
                    <strong>RNA (Nicht-Teilnehmer):</strong>{' '}
                    {rnaPreview
                      ? `${rnaPreview.registrations} Registrierungen - ${rnaPreview.attendees} Teilnehmer - ${rnaPreview.purchasers} Käufer = ${rnaPreview.segmentSize} Empfänger`
                      : 'Lädt...'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2">
                    <strong>ANO (Teilnehmer ohne Kauf):</strong>{' '}
                    {anoPreview
                      ? `${anoPreview.attendees} Teilnehmer - ${anoPreview.purchasers} Käufer = ${anoPreview.segmentSize} Empfänger`
                      : 'Lädt...'}
                  </Typography>
                </Box>
              </Box>
            </Alert>
          </Grid>
        )}

        {/* Segment Tabs */}
        <Grid item xs={12}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeSegmentTab} onChange={handleSegmentTabChange}>
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    RNA (Nicht-Teilnehmer)
                    {rnaPreview && <Chip label={rnaPreview.segmentSize} size="small" />}
                    {!rnaSegment.enabled && (
                      <Chip label="Deaktiviert" size="small" color="default" />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    ANO (Teilnehmer ohne Kauf)
                    {anoPreview && <Chip label={anoPreview.segmentSize} size="small" />}
                    {!anoSegment.enabled && (
                      <Chip label="Deaktiviert" size="small" color="default" />
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>
        </Grid>

        {/* Segment Content */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {/* Enable Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={activeSegment.enabled}
                  onChange={(e) =>
                    setActiveSegment({
                      ...activeSegment,
                      enabled: e.target.checked,
                    })
                  }
                  disabled={activeSegment.status !== 'draft'}
                />
              }
              label="Segment aktiviert"
              sx={{ mb: 2 }}
            />

            {/* Segment Preview */}
            {currentPreview && (
              <Alert severity={currentPreview.segmentSize > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{currentPreview.segmentSize}</strong> Empfänger im Segment
                  {currentPreview.segmentSize === 0 && ' - Segment wird nicht gesendet'}
                </Typography>
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Landing URL */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={activeSegmentTab === 0 ? 'Video-Replay URL' : 'Bestellseite URL'}
                  value={activeSegment.landingPageUrl}
                  onChange={(e) =>
                    setActiveSegment({
                      ...activeSegment,
                      landingPageUrl: e.target.value,
                    })
                  }
                  onBlur={(e) => {
                    // Strip query parameters on blur - they're replaced with tracking banner on send
                    const cleanUrl = e.target.value.split('?')[0];
                    if (cleanUrl !== e.target.value) {
                      setActiveSegment({
                        ...activeSegment,
                        landingPageUrl: cleanUrl,
                      });
                    }
                    // Validate URL reachability
                    if (cleanUrl.trim()) {
                      validateUrl('landing_page_url', cleanUrl);
                    }
                  }}
                  placeholder="https://..."
                  disabled={activeSegment.status !== 'draft'}
                  error={
                    landingPageUrlState.reachable === false &&
                    !landingPageUrlState.cross_domain_redirect
                  }
                  helperText={
                    landingPageUrlState.validating
                      ? 'URL wird geprüft...'
                      : landingPageUrlState.reachable === true
                        ? 'URL erreichbar'
                        : landingPageUrlState.cross_domain_redirect
                          ? `Weiterleitung zu anderer Domain: ${landingPageUrlState.redirect_url}`
                          : landingPageUrlState.error
                            ? landingPageUrlState.error
                            : 'URL-Parameter werden automatisch durch Tracking-Banner ersetzt'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {landingPageUrlState.validating && <CircularProgress size={18} />}
                        {landingPageUrlState.reachable === true &&
                          !landingPageUrlState.cross_domain_redirect && (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        {landingPageUrlState.cross_domain_redirect && (
                          <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                        )}
                        {landingPageUrlState.reachable === false &&
                          !landingPageUrlState.cross_domain_redirect && (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Delay Configuration */}
              <Grid item xs={6} md={3}>
                <TextField
                  type="number"
                  label="Tage nach Webinar"
                  value={activeSegment.delayDays}
                  onChange={(e) =>
                    setActiveSegment({
                      ...activeSegment,
                      delayDays: parseInt(e.target.value) || 1,
                    })
                  }
                  inputProps={{ min: 1, max: 30 }}
                  fullWidth
                  disabled={activeSegment.status !== 'draft'}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  type="time"
                  label="Uhrzeit"
                  value={activeSegment.delayTime}
                  onChange={(e) =>
                    setActiveSegment({
                      ...activeSegment,
                      delayTime: e.target.value,
                    })
                  }
                  fullWidth
                  disabled={activeSegment.status !== 'draft'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Scheduled datetime preview */}
              {selectedParent?.webinar_date && (
                <Grid item xs={12} md={6}>
                  <Alert severity="info" icon={false}>
                    <Typography variant="body2">
                      Webinar: {moment.utc(selectedParent.webinar_date).format('DD.MM.YYYY HH:mm')}
                      <br />
                      Erste E-Mail:{' '}
                      {moment(selectedParent.webinar_date)
                        .add(activeSegment.delayDays, 'days')
                        .format('DD.MM.YYYY')}{' '}
                      um {activeSegment.delayTime}
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {/* Email Tabs */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" mt={2} mb={1}>
                  E-Mails
                </Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={activeEmailTab} onChange={(_, v) => setActiveEmailTab(v)}>
                    {activeSegment.emails.map((email, index) => (
                      <Tab
                        key={index}
                        label={`E-Mail ${index + 1}${email.delayOffsetDays > 0 ? ` (+${email.delayOffsetDays}d)` : ''}`}
                      />
                    ))}
                    {activeSegment.emails.length < 3 && activeSegment.status === 'draft' && (
                      <Tab label="+ Hinzufügen" onClick={addEmail} />
                    )}
                  </Tabs>
                </Box>

                {activeSegment.emails.map((email, index) => (
                  <Box key={index} hidden={activeEmailTab !== index}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Betreff"
                          value={email.subject}
                          onChange={(e) => updateEmail(index, 'subject', e.target.value)}
                          disabled={activeSegment.status !== 'draft'}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Absender"
                          value={email.fromName}
                          onChange={(e) => updateEmail(index, 'fromName', e.target.value)}
                          disabled={activeSegment.status !== 'draft'}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="number"
                          label="Zusätzliche Verzögerung (Tage)"
                          value={email.delayOffsetDays}
                          onChange={(e) =>
                            updateEmail(index, 'delayOffsetDays', parseInt(e.target.value) || 0)
                          }
                          inputProps={{ min: 0, max: 14 }}
                          fullWidth
                          disabled={activeSegment.status !== 'draft'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Preview Text"
                          value={email.previewText}
                          onChange={(e) => updateEmail(index, 'previewText', e.target.value)}
                          disabled={activeSegment.status !== 'draft'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" mb={1}>
                          HTML-Inhalt
                        </Typography>
                        <Typography variant="caption" color="text.secondary" mb={1} display="block">
                          Platzhalter: {'{FOLLOWUP_LINK}'}, {'{WEBINAR_TITLE}'}, {'{WEBINAR_DATE}'},{' '}
                          {'{EMAIL}'}
                        </Typography>
                        <Editor
                          onInit={(evt, editor) => {
                            const key = activeSegmentTab === 0 ? `rna_${index}` : `ano_${index}`;
                            editorRefs.current[key] = editor;
                          }}
                          // Selbst gehostet aus public/tinymce (v8.2.2) — kein Cloud-Key, kein
                          // Request an cdn.tiny.cloud. Dieser Editor haengt NICHT am gemeinsamen
                          // components/TinyMCEEditor-Wrapper, deshalb steht die Umstellung hier
                          // ein zweites Mal.
                          tinymceScriptSrc="/tinymce/tinymce.min.js"
                          // Ohne Lizenzschluessel deaktiviert TinyMCE 7+ den Editor. Als PROP,
                          // nicht in `init` — dort ueberschreibt der Wrapper den Wert.
                          licenseKey="gpl"
                          initialValue={email.htmlBody}
                          key={`${activeSegmentTab}_${index}`}
                          init={{
                            height: 400,
                            menubar: true,
                            plugins: [
                              'advlist',
                              'autolink',
                              'lists',
                              'link',
                              'image',
                              'charmap',
                              'anchor',
                              'searchreplace',
                              'visualblocks',
                              'code',
                              'fullscreen',
                              'insertdatetime',
                              'media',
                              'table',
                              'preview',
                              'help',
                              'wordcount',
                            ],
                            toolbar:
                              'undo redo | blocks | ' +
                              'bold italic forecolor backcolor | alignleft aligncenter ' +
                              'alignright alignjustify | bullist numlist outdent indent | ' +
                              'link image | removeformat | code | help',
                            content_style:
                              'body { font-family: Arial, sans-serif; font-size: 14px; }',
                            valid_elements: '*[*]',
                            extended_valid_elements: '*[*]',
                            valid_styles: { '*': '*' },
                          }}
                          disabled={activeSegment.status !== 'draft'}
                        />
                        {/* Action buttons */}
                        <Box display="flex" gap={1} mt={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              importLoading ? <CircularProgress size={16} /> : <UploadFileIcon />
                            }
                            onClick={() => setImportModalOpen(true)}
                            disabled={activeSegment.status !== 'draft' || importLoading}
                          >
                            Aus HTML importieren
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              loadingPreview ? <CircularProgress size={16} /> : <VisibilityIcon />
                            }
                            onClick={handlePreview}
                            disabled={!activeSegment.id || !email.htmlBody || loadingPreview}
                          >
                            Vorschau
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SendIcon />}
                            onClick={() => setShowTestDialog(true)}
                            disabled={!activeSegment.id || !email.subject || !email.htmlBody}
                          >
                            Test senden
                          </Button>
                        </Box>
                      </Grid>
                      {/* Spam-Score / Content-Quality Panel */}
                      <Grid item xs={12}>
                        <SpamScorePanel
                          editorType="webinar_followup"
                          referenceId={index + 1}
                          campaignId={campaignId ?? null}
                          subject={email.subject || ''}
                          preheader={email.previewText || ''}
                          body={email.htmlBody || ''}
                          fromName={email.fromName || ''}
                          onResultChange={(r) =>
                            handleSpamResultChange(
                              `${activeSegmentTab === 0 ? 'rna' : 'ano'}:${index}`,
                              r,
                            )
                          }
                        />
                      </Grid>
                      {activeSegment.emails.length > 1 && activeSegment.status === 'draft' && (
                        <Grid item xs={12}>
                          <Button color="error" onClick={() => removeEmail(index)}>
                            E-Mail {index + 1} entfernen
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Validation Warnings */}
      {status === 'draft' && validationIssues.length > 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <AlertTitle>Kampagne kann nicht geplant werden</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            {validationIssues.map((issue, idx) => (
              <li key={idx}>{issue}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Actions */}
      <Box display="flex" gap={2} mt={3} justifyContent="flex-end">
        <Button variant="outlined" onClick={onCancel}>
          Zurück
        </Button>
        {(status === 'draft' || status === 'awaiting_webinar') && (
          <>
            <Button
              variant="contained"
              onClick={() => handleSave()}
              disabled={saving || hasUnreachableUrls() || isValidating()}
            >
              {saving ? <CircularProgress size={20} /> : 'Speichern'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSchedule}
              disabled={!canSchedule || !campaignId || saving || scheduleSuccess}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
              title={validationIssues.length > 0 ? validationIssues.join('\n') : ''}
            >
              {saving
                ? 'Wird geplant...'
                : status === 'awaiting_webinar'
                  ? 'Erneut prüfen'
                  : 'Planen & Senden'}
            </Button>
          </>
        )}
        {status === 'awaiting_webinar' && campaignId && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<CancelIcon />}
            onClick={() => setShowCancelDialog(true)}
            disabled={cancelling}
          >
            Abbrechen
          </Button>
        )}
        {(status === 'scheduled' || status === 'sent') && campaignId && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<CancelIcon />}
            onClick={() => setShowCancelDialog(true)}
            disabled={cancelling}
          >
            Kampagne stornieren
          </Button>
        )}
        {status === 'cancelled' && campaignId && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleReschedule}
            disabled={rescheduling}
          >
            {rescheduling ? 'Wird freigegeben...' : 'Zur Bearbeitung freigeben'}
          </Button>
        )}
      </Box>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>Nachfass-Kampagne stornieren?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            <strong>{name}</strong>
          </Typography>
          <Typography variant="body2">
            Alle geplanten E-Mails in Iterable werden storniert. Bereits gesendete E-Mails können
            nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)} disabled={cancelling}>
            Zurück
          </Button>
          <Button
            onClick={handleCancelCampaign}
            color="warning"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? 'Wird storniert...' : 'Stornieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Template Modal */}
      <Dialog
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>HTML Template importieren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Importieren Sie eine HTML-Datei aus Mailchimp oder Iterable Export.
          </Typography>

          <Box mb={3}>
            <input
              type="file"
              accept=".html,.htm"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <Button
              variant="contained"
              startIcon={importLoading ? <CircularProgress size={16} /> : <UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={importLoading}
            >
              HTML-Datei auswählen
            </Button>
          </Box>

          {detectedUrls.length > 0 && (
            <Box mb={3}>
              <Typography variant="subtitle2" mb={1}>
                URLs zum Ersetzen durch {'{FOLLOWUP_LINK}'}:
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {detectedUrls.map((url, idx) => (
                  <Box key={idx} display="flex" alignItems="center">
                    <Checkbox
                      checked={selectedUrls.includes(url)}
                      onChange={() =>
                        setSelectedUrls((prev) =>
                          prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
                        )
                      }
                    />
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {url.length > 80 ? url.substring(0, 80) + '...' : url}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {importedContent && (
            <Box>
              <Typography variant="subtitle2" mb={1}>
                Extrahierter Inhalt:
              </Typography>
              <Box
                sx={{
                  maxHeight: 300,
                  overflow: 'auto',
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#fafafa',
                }}
                dangerouslySetInnerHTML={{ __html: importedContent }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImportModalOpen(false);
              setImportedContent(null);
              setDetectedUrls([]);
              setSelectedUrls([]);
            }}
          >
            Abbrechen
          </Button>
          {importedContent && (
            <Button variant="contained" onClick={handleConfirmImport}>
              Importieren {selectedUrls.length > 0 && `(${selectedUrls.length} URLs ersetzen)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">E-Mail Vorschau</Typography>
            <ToggleButtonGroup
              value={previewWidth}
              exclusive
              onChange={(_, v) => v && setPreviewWidth(v)}
              size="small"
            >
              <ToggleButton value="desktop">Desktop (640px)</ToggleButton>
              <ToggleButton value="mobile">Mobile (375px)</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingPreview ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center">
              <iframe
                srcDoc={previewHtml}
                title="E-Mail Vorschau"
                style={{
                  width: previewWidth === 'desktop' ? '640px' : '375px',
                  height: '500px',
                  border: '1px solid #ccc',
                }}
                sandbox="allow-same-origin"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewDialog(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Test Send Dialog */}
      <Dialog
        open={showTestDialog}
        onClose={() => setShowTestDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SendIcon />
            Test-E-Mail senden
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Senden Sie eine Test-E-Mail über Iterable (FT).
          </Typography>
          <TextField
            fullWidth
            label="E-Mail-Adresse"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="ihre@email.de"
            helperText="Die Test-E-Mail wird an diese Adresse gesendet"
            sx={{ mb: 2 }}
          />
          <Alert severity="info">Test-E-Mails werden immer über FT/Iterable gesendet.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTestDialog(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSendTestEmail}
            disabled={!testEmail || sendingTest}
            startIcon={sendingTest ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {sendingTest ? 'Wird gesendet...' : 'Test senden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spam-Score Hard-Block Override Dialog */}
      <Dialog
        open={spamOverrideOpen}
        onClose={() => {
          spamOverridePayload?.resolve(false);
          setSpamOverrideOpen(false);
          setSpamOverridePayload(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#dc2626', fontWeight: 'bold' }}>
          ⚠️ Content-Quality-Check: kritischer Score
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {spamOverridePayload != null && (
              <>
                {spamOverridePayload.segmentLabel} E-Mail {spamOverridePayload.emailIndex + 1}{' '}
                enthält{' '}
                <strong>
                  {spamResults[
                    `${spamOverridePayload.segmentLabel === 'RNA' ? 'rna' : 'ano'}:${spamOverridePayload.emailIndex}`
                  ]?.layer1.redCount ?? 0}{' '}
                  Red-Flag-Wörter
                </strong>
                . Diese Kombination erhöht das Risiko als Spam eingestuft zu werden spürbar.
                <br />
                <br />
                Du kannst die Mail trotzdem speichern — die Entscheidung wird zu Auswertungs-Zwecken
                protokolliert. Alternativ: Text überarbeiten und neu speichern.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              spamOverridePayload?.resolve(false);
              setSpamOverrideOpen(false);
              setSpamOverridePayload(null);
            }}
          >
            Abbrechen & überarbeiten
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              spamOverridePayload?.resolve(true);
              setSpamOverrideOpen(false);
              setSpamOverridePayload(null);
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spam-Score Advisory Dialog (non-blocking) */}
      <Dialog
        open={spamAdvisoryOpen}
        onClose={() => {
          spamAdvisoryPayload?.resolve(false);
          setSpamAdvisoryOpen(false);
          setSpamAdvisoryPayload(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#f97316', fontWeight: 'bold' }}>
          💡 Content-Hinweise vor dem Speichern
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {spamAdvisoryPayload != null && (
              <>
                {spamAdvisoryPayload.segmentLabel} E-Mail {spamAdvisoryPayload.emailIndex + 1} hat{' '}
                <strong>
                  {spamAdvisoryPayload.warningCount} Content-Hinweis
                  {spamAdvisoryPayload.warningCount === 1 ? '' : 'e'}
                </strong>{' '}
                (z.B. Red-Flag-Wörter, Urgency-Muster oder Caps-Runs).
                <br />
                <br />
                <strong>Empfehlung vor dem Versand:</strong>
                <ol style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                  <li>Die markierten Hinweise im Content-Quality-Panel prüfen und bereinigen.</li>
                  {!spamAdvisoryPayload.llmChecked && (
                    <li>
                      <strong>LLM-Check starten</strong> — liefert kontextuelle Analyse + konkrete
                      Umformulierungs-Vorschläge.
                    </li>
                  )}
                  {spamAdvisoryPayload.llmChecked && (
                    <li>LLM-Rewrite-Vorschläge übernehmen und danach Check wiederholen.</li>
                  )}
                </ol>
                Bei niedriger Absender-Reputation erhöhen diese Signale das Spam-Risiko bei Gmail
                und GMX. Du kannst trotzdem speichern — die Entscheidung wird protokolliert.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              spamAdvisoryPayload?.resolve(false);
              setSpamAdvisoryOpen(false);
              setSpamAdvisoryPayload(null);
            }}
          >
            Zurück & überarbeiten
          </Button>
          <Button
            onClick={() => {
              spamAdvisoryPayload?.resolve(true);
              setSpamAdvisoryOpen(false);
              setSpamAdvisoryPayload(null);
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FollowupEditor;
